import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Globe2, ShieldCheck, TrendingUp, Package, Coins } from "lucide-react";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import MagneticButton from "../components/MagneticButton";
import GoldParticles from "../components/GoldParticles";
import { COUNTRIES, HERO_IMAGES, waLink } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = ["Singapore", "Malaysia", "UAE", "United Kingdom", "United States", "Australia", "Hong Kong", "Thailand"];

const WHY = [
  { icon: Coins, t: "Price Advantage", d: "Direct manufacturing cost from India — cut 20–40% off your landed prices." },
  { icon: ShieldCheck, t: "Certified Quality", d: "BIS-hallmarked, IGI/GIA certified — every piece, every time." },
  { icon: Package, t: "5,000+ Designs", d: "From heritage polki to modern minimalism — curated for every shelf." },
  { icon: TrendingUp, t: "No MOQ Barriers", d: "Start small, scale fast — we grow with you, not ahead of you." },
  { icon: Globe2, t: "Logistics Handled", d: "Customs, insurance, door-delivery — we run the supply chain." },
];

const STEPS = [
  { n: "01", t: "Register", d: "Fill the partner form. We verify and assign a dedicated sourcing manager." },
  { n: "02", t: "Browse & Select", d: "Access the full catalogue plus private curated edits built for your market." },
  { n: "03", t: "We Source & Ship", d: "Quality-checked at source, documented, insured, and delivered to your door." },
];

export default function Retailers() {
  const [form, setForm] = React.useState({ name: "", store: "", email: "", country: "", category: "", volume: "" });
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.store || !form.country) {
      toast.error("Name, store and country are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { kind: "partner", ...form });
      toast.success("Application received. We'll be in touch within 24 hours.");
      const msg = `Hi Blue Velvet, new partner application:\nName: ${form.name}\nStore: ${form.store}\nCountry: ${form.country}\nCategory: ${form.category}\nMonthly volume: ${form.volume}`;
      window.open(waLink(msg), "_blank", "noopener,noreferrer");
      setForm({ name: "", store: "", email: "", country: "", category: "", volume: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <>
      <section data-testid="retailers-hero" className="relative min-h-[75vh] pt-36 pb-24 overflow-hidden grain border-b border-[#B5896E]/10">
        <GoldParticles count={35} />
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.retailersBanner} alt="" loading="lazy" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FAF6F0]/80 via-[#FAF6F0]/85 to-[#FAF6F0]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="animate-fade-up max-w-4xl">
            <div className="caption text-[#B5896E] mb-5 flex items-center gap-3"><span className="w-8 h-[1px] bg-[#B5896E]" />For Retailers</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02]">Your global jewelry partner,<br /><em className="italic text-[#B5896E]">rooted in India.</em></h1>
            <p className="mt-8 text-base lg:text-lg text-[#4A3F35]/75 leading-relaxed max-w-2xl">For independent retailers across Singapore, Malaysia, UAE, the UK and USA — Indian craftsmanship, certified quality, no MOQ barriers, logistics included.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              {REGIONS.map((r) => (
                <span key={r} className="px-4 py-2 gold-border bg-[#FFFFFF]/50 caption">{r}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="retailers-why" className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal><SectionHeading eyebrow="Why Blue Velvet" title={<>Built so you <em className="italic text-[#B5896E]">compete — and win.</em></>} /></Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.t} delay={i * 80}>
                  <div className="p-8 gold-border bg-[#FFFFFF]/50 h-full hover:border-[#B5896E]/60 transition-colors">
                    <Icon size={26} className="text-[#B5896E] mb-5" />
                    <h4 className="font-display text-lg mb-2">{w.t}</h4>
                    <p className="text-sm text-[#4A3F35]/65 leading-relaxed">{w.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="retailers-india" className="py-20 lg:py-24 bg-[#E8DDD0]/25 border-y border-[#B5896E]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal><SectionHeading eyebrow="India Relevance" title={<>A 30-year heritage, <em className="italic text-[#B5896E]">certified for the world.</em></>} subtitle="Trusted by 300+ Indian retailers. BIS-hallmarked. GJEPC and MSME registered. Export-ready documentation on every shipment." /></Reveal>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {["BIS Hallmarked", "GIA Certified", "IGI Verified", "GJEPC Member"].map((b) => (
              <div key={b} className="p-6 gold-border bg-[#FFFFFF]/60 text-center">
                <ShieldCheck size={20} className="text-[#B5896E] mx-auto mb-3" />
                <div className="caption">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="retailers-how" className="py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <Reveal><SectionHeading eyebrow="How It Works" title={<>Three steps. That's <em className="italic text-[#B5896E]">it.</em></>} /></Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 120}>
                <div className="relative p-10 gold-border bg-[#FFFFFF]/40 h-full">
                  <div className="font-display text-6xl text-[#B5896E]/40 leading-none">{s.n}</div>
                  <h4 className="font-display text-2xl mt-4 mb-3">{s.t}</h4>
                  <p className="text-sm text-[#4A3F35]/65 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="retailers-form" className="py-24 lg:py-32 bg-[#E8DDD0]/25 border-y border-[#B5896E]/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <SectionHeading eyebrow="Partner Application" title="Apply now" />
              <p className="mt-6 text-[#4A3F35]/70 leading-relaxed text-sm">Submit your details or open a direct WhatsApp conversation — whichever is faster for you.</p>
              <div className="mt-8">
                <MagneticButton testId="retailers-whatsapp-direct" variant="outline" href={waLink("Hi Blue Velvet, I'd like to apply as a partner retailer.")}><MessageCircle size={14} className="mr-2" /> WhatsApp Instead</MagneticButton>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-3">
            <form data-testid="partner-form" onSubmit={submit} className="p-8 lg:p-10 gold-border bg-[#FFFFFF]/60 space-y-5 rounded-sm">
              <Field label="Your name *">
                <input data-testid="partner-field-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT} />
              </Field>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Store name *">
                  <input data-testid="partner-field-store" required value={form.store} onChange={(e) => set("store", e.target.value)} className={INPUT} />
                </Field>
                <Field label="Email">
                  <input data-testid="partner-field-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT} />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Country *">
                  <select data-testid="partner-field-country" required value={form.country} onChange={(e) => set("country", e.target.value)} className={INPUT}>
                    <option value="">Select country</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Category interest">
                  <select data-testid="partner-field-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={INPUT}>
                    <option value="">Select category</option>
                    <option>Rings</option><option>Pendants</option><option>Earrings</option><option>Necklaces</option><option>Full range</option>
                  </select>
                </Field>
              </div>
              <Field label="Monthly volume (USD)">
                <select data-testid="partner-field-volume" value={form.volume} onChange={(e) => set("volume", e.target.value)} className={INPUT}>
                  <option value="">Select volume</option>
                  <option>Under $5,000</option><option>$5,000 – $25,000</option><option>$25,000 – $100,000</option><option>$100,000+</option>
                </select>
              </Field>
              <div className="pt-2">
                <MagneticButton testId="partner-submit" type="submit" variant="primary">{loading ? "Submitting…" : "Submit application"}</MagneticButton>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

const INPUT = "w-full bg-[#FAF6F0] border border-[#B5896E]/20 text-[#4A3F35] px-4 py-3 rounded-sm focus:outline-none focus:border-[#B5896E] text-sm transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="caption text-[#4A3F35]/60 mb-2 block">{label}</span>
      {children}
    </label>
  );
}
